import { z } from 'zod';
import { emailSchema, passwordSchema } from './auth.js';
import { uuid } from './common.js';
import { OrganizationStatus } from './enums.js';
const trimmed = (max) => z.string().trim().max(max);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));
const hexColor = () => z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** Shared shape for the org's own color overrides (`organizations.colors`)
 * AND the platform-wide defaults used when an org hasn't set a given field
 * (`platform_settings.design.colors`, see platform-settings.ts) — every
 * field optional/unset means "inherit from the next layer down"
 * (org → platform default → hard-coded app default, see AppShell.tsx /
 * OrgDesignPage.tsx). */
export const organizationColors = z
    .object({
    primary: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    headerBackground: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    headerText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    /** px; unset falls back to the layout's own default (~56px). */
    headerHeight: z.number().int().min(40).max(160).optional(),
    /** Whether the header's own border is drawn at all (§ Nutzeranfrage —
     * the always-on built-in border read as an unwanted "weiße Umrandung"
     * against a dark `headerBackground`/background image). Unset falls back
     * to the platform default, then to the built-in `false` (AppShell.tsx). */
    headerBorderEnabled: z.boolean().optional(),
    /** Only drawn when `headerBorderEnabled` resolves to `true`. */
    headerBorderColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    /** px; the boxed layout's max content width (header + content, see
     * AppShell.tsx). Unset falls back to the built-in default (1152px,
     * Tailwind's `max-w-6xl`). */
    boxedWidth: z.number().int().min(600).max(2400).optional(),
    /** Whether the app renders in the centered "boxed" layout (fixed max
     * width, background image/color visible on the sides) at all — `false`
     * renders full-width instead, with no background layer and `boxedWidth`/
     * `pageBackgroundColor`/background image having no effect. Unset falls
     * back to the platform default, then to the built-in `true` (today's
     * always-boxed look), see AppShell.tsx. */
    boxedLayoutEnabled: z.boolean().optional(),
    sidebarBackground: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    /** 0 (fully transparent) – 1 (fully opaque); lets the background image show through. */
    sidebarOpacity: z.number().min(0).max(1).optional(),
    /** Present = a linear gradient from sidebarBackground to this color, at
     * sidebarGradientAngle, instead of a flat fill. */
    sidebarBackgroundTo: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    /** CSS gradient angle in degrees (0 = bottom-to-top, 90 = left-to-right). */
    sidebarGradientAngle: z.number().min(0).max(360).optional(),
    contentBackground: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    contentOpacity: z.number().min(0).max(1).optional(),
    contentBackgroundTo: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    contentGradientAngle: z.number().min(0).max(360).optional(),
    /** Solid fallback for the fixed, non-scrolling "boxed layout" background
     * (see AppShell) when no background image is set — image and color are
     * mutually exclusive, the image wins if both happen to be set at the
     * same level (own or platform default). */
    pageBackgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
})
    .partial();
/** Per-org branding for the public registration page (`/invite/:token`) —
 * reached only via an invite link, so the org is already known; there is no
 * equivalent for the login page (no org context before authenticating).
 * The club's existing logo (`logoFileId`) is reused as-is here, not
 * reconfigured — only the elements genuinely specific to this page live
 * here: a large left-hand image, heading/welcome copy, and its own color
 * scheme (independent of the app's own `colors.*`, which this page — being
 * outside `AppShell` — never renders with). */
export const authPageDesign = z
    .object({
    heading: optionalText(200),
    welcomeText: optionalText(1000),
    primaryColor: hexColor().optional(),
    accentColor: hexColor().optional(),
    backgroundColor: hexColor().optional(),
})
    .partial();
/** Vereinsstammdaten — editable by an org with `organization.edit`. */
export const organizationProfile = z.object({
    name: trimmed(200).min(1),
    shortName: optionalText(50),
    colors: organizationColors.optional(),
    /** Dark-Mode-Variante von `colors` — dieselben Felder, nur wirksam wenn der
     * Betrachter Dark Mode aktiv hat. Unabhängig speicherbar, siehe
     * docs/administration.md. */
    colorsDark: organizationColors.optional(),
    authDesign: authPageDesign.optional(),
    street: optionalText(200),
    houseNumber: optionalText(20),
    zip: optionalText(20),
    city: optionalText(200),
    country: optionalText(2),
    phone: optionalText(50),
    email: emailSchema.optional(),
    website: z.string().trim().url().max(300).optional().or(z.literal('').transform(() => undefined)),
    contactPerson: optionalText(200),
    clubNumber: optionalText(50),
    federation: optionalText(100),
    externalClubId: optionalText(100),
    externalClubUrl: z
        .string()
        .trim()
        .url()
        .max(500)
        .optional()
        .or(z.literal('').transform(() => undefined)),
});
export const updateOrganizationRequest = organizationProfile.partial();
export const MAIL_TRANSPORTS = ['smtp', 'api', 'graph'];
/** One mail connection's details — klassisches SMTP, die HTTP-API eines
 * Anbieters (aktuell: Postal, https://docs.postalserver.io/developer/api —
 * `POST /api/v1/send/message` mit `X-Server-API-Key`-Header) oder Microsoft
 * 365 über die Graph-API (OAuth2-Client-Credentials-Flow, siehe
 * docs/mail.md). Shared shape for both the platform-wide default
 * (`platform_settings.mail`) and a per-org override
 * (`organization_settings.mail.custom`). `password`/`apiKey`/`clientSecret`
 * are write-only from the client's perspective: PATCH may set them, but GET
 * never echoes the stored value back — see
 * `maskMailFields`/`MailFieldsResponse` below. */
export const mailFields = z.object({
    transport: z.enum(MAIL_TRANSPORTS).default('smtp'),
    // SMTP (`transport: 'smtp'`)
    host: optionalText(255),
    port: z.number().int().min(1).max(65535).default(587),
    secure: z.boolean().default(false),
    user: optionalText(255),
    password: optionalText(500),
    // HTTP API (`transport: 'api'`) — e.g. a Postal server's own base URL
    // (`https://postal.example.com`, without the `/api/v1/...` path) and its
    // per-server API key from Postal's "Credentials" tab.
    apiUrl: optionalText(500),
    apiKey: optionalText(500),
    // Microsoft 365 / Graph-API (`transport: 'graph'`) — Entra-ID-App-
    // Registrierung mit Client-Credentials-Flow, eingeschränkt per Exchange-
    // Online-"Application Access Policy" auf ein einzelnes Postfach (siehe
    // docs/mail.md). Das Sendepostfach ist absichtlich kein eigenes Feld,
    // sondern das ohnehin vorhandene `fromAddress` weiter unten.
    tenantId: optionalText(255),
    clientId: optionalText(255),
    clientSecret: optionalText(500),
    /** Reines Hinweisfeld fürs Admin-UI (Ablauf-Warnung) — hat keine
     * funktionale Auswirkung auf den Mailversand selbst. ISO-Datum. */
    clientSecretExpiresAt: optionalText(20),
    /** Ob gesendete Mails im Postfach des Absenders unter "Gesendete
     * Elemente" abgelegt werden (Graph `sendMail`-Parameter
     * `saveToSentItems`). */
    graphSaveToSentItems: z.boolean().default(true),
    /** Envelope/header "From" address — falls back to the app-wide `MAIL_FROM`
     * env var when unset, same as every other field here falling through to
     * the next layer down (org → platform → env, see `resolveMailConfig` in
     * `@bm/db`). Bei `transport: 'graph'` zugleich das Postfach, über das via
     * Graph gesendet wird (`/users/{fromAddress}/sendMail`). */
    fromAddress: emailSchema.optional(),
    fromName: optionalText(200),
});
export function maskMailFields(fields) {
    const { password, apiKey, clientSecret, ...rest } = fields;
    return { ...rest, hasPassword: !!password, hasApiKey: !!apiKey, hasClientSecret: !!clientSecret };
}
/** A club's own mail-connection override (Verein › SMTP Server) —
 * `mode: 'global'` (the default) means "use the platform-wide default",
 * `custom` is only consulted once an org explicitly switches to
 * `mode: 'custom'`. Keeping `custom` around even while `mode: 'global'` lets
 * an admin flip back and forth without re-entering the values each time. */
export const orgMailSettings = z.object({
    mode: z.enum(['global', 'custom']).default('global'),
    custom: mailFields.default({}),
});
export const testMailRequest = z.object({ to: emailSchema });
/** Vereinsadministration (Benutzerverwaltung, „Abstimmungen zurücksetzen") —
 * löscht Saisonspiel-Rückmeldungen, Trainings-Anmeldungen und
 * Verlegungsabstimmungs-Stimmen. `memberId` fehlend = für den ganzen Verein,
 * gesetzt = nur für dieses eine Mitglied. Siehe docs/administration.md. */
export const resetResponsesRequest = z.object({
    memberId: uuid.optional(),
});
/** Organisation settings — vocab lists etc. kept configurable, not in code. */
export const organizationSettings = z.object({
    locale: z.string().default('de-DE'),
    timezone: z.string().default('Europe/Berlin'),
    ageClasses: z.array(z.string()).default(['U9', 'U11', 'U13', 'U15', 'U17', 'U19', 'Senioren']),
    memberTypes: z.array(z.string()).default(['Aktiv', 'Passiv', 'Ehrenmitglied']),
    matchFormats: z
        .array(z.object({
        key: z.string(),
        name: z.string(),
        disciplines: z.array(z.enum(['HE', 'DE', 'HD', 'DD', 'MX'])),
    }))
        .default([]),
    featureFlags: z.record(z.boolean()).default({}),
    /** nav item key -> lucide-react icon name, e.g. { members: 'Users' } */
    navIcons: z.record(z.string()).default({}),
    /** Minutes between automatic Turnier.de match syncs; null = disabled (manual
     * only). Minimum 60 to stay within "rücksichtsvolles Abrufen" (see
     * docs/turnier-de.md) — this isn't a live scoreboard, hourly is plenty. */
    turnierDeSyncIntervalMinutes: z.number().int().min(60).max(10_080).nullable().default(null),
    /** How many days a generated self-registration invite link
     * (organization_invites) stays valid — configurable per org instead of a
     * fixed constant, see docs/members.md. */
    inviteValidityDays: z.number().int().min(1).max(365).default(14),
    /** Verwaltung › Benutzerverwaltung › "Erweiterte Einstellungen": nach wie
     * vielen Monaten ohne Login ein Benutzerkonto automatisch vollständig
     * gelöscht wird (Mitgliedsdatensatz + Zugang, nicht nur deaktiviert) —
     * null = deaktiviert. Ausgeführt vom täglichen `maintenance`-Sweep-Job
     * (`runInactiveUserSweep`, `@bm/sync`), nie live bei einem Request. Siehe
     * docs/members.md. */
    inactiveUserDeleteAfterMonths: z.number().int().min(1).max(60).nullable().default(null),
    /** This org's own SMTP override (Verein › SMTP Server) — see
     * `orgMailSettings` above and docs/mail.md. */
    mail: orgMailSettings.default({ mode: 'global', custom: {} }),
});
/** Platform-admin: create a new organisation (tenant). */
export const createOrganizationRequest = z.object({
    name: trimmed(200).min(1),
    shortName: optionalText(50),
    status: OrganizationStatus.default('active'),
});
/** Platform-admin: assign (or invite) a club administrator for an organisation.
 * `firstName`/`lastName` are required — a club admin always gets a linked
 * member record (created, or linked if the account already exists) so they
 * automatically show up in this org's Mitgliederliste and can use
 * member-only features (training responses, season votes, …) themselves. */
export const assignClubAdminRequest = z.object({
    email: emailSchema,
    /** Set to hand out working credentials immediately, skipping the invite email. */
    password: passwordSchema.optional(),
    firstName: trimmed(200).min(1),
    lastName: trimmed(200).min(1),
});
export const setOrganizationStatusRequest = z.object({
    status: OrganizationStatus,
});
/** Platform-admin: edit a club's core identity fields from the "Vereine" list —
 * a narrower sibling of `updateOrganizationRequest` (which is tenant-facing and
 * covers the full profile); the platform list only ever shows/edits name + Kurzname. */
export const platformUpdateOrganizationRequest = z.object({
    name: trimmed(200).min(1),
    shortName: optionalText(50),
});
/** Platform-admin: create a new platform administrator, or grant the flag to an
 * existing account found by email. `password` is required only for the
 * new-account branch (enforced in the service, not here — the existing
 * account's own password is left untouched). */
export const createPlatformAdminRequest = z.object({
    email: emailSchema,
    password: passwordSchema.optional(),
});
/** Platform-admin: reset a platform/club administrator's password.
 * `organizationId` on the `email` mode is only meaningful for a *club*
 * admin (a platform admin has none) — when set, the reset e-mail goes out
 * through that org's own SMTP override if it has one configured, instead of
 * always using the platform-wide default. */
export const resetAdminPasswordRequest = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('email'), organizationId: z.string().uuid().optional() }),
    z.object({ mode: z.literal('manual'), password: passwordSchema }),
]);
//# sourceMappingURL=organization.js.map